/**
 * Delete Account Modal — confirmation dialog for soft-deleting user account.
 * 30-day grace period, can cancel by logging in within 30 days.
 */

import React, { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { AlertTriangle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@turnifly/shared/ui'
import { useDeleteProfile } from '@turnifly/shared/hooks'
import { useAuthStore } from '@turnifly/shared/stores'

interface DeleteAccountModalProps {
  isOpen: boolean
  onClose: () => void
}

export const DeleteAccountModal: React.FC<DeleteAccountModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const deleteProfile = useDeleteProfile()
  const [confirmText, setConfirmText] = useState('')
  const confirmWord = t('deleteAccount.confirmWord')

  const isConfirmed = confirmText.toLowerCase() === confirmWord.toLowerCase()

  const handleDelete = (): void => {
    if (!user?.id || !isConfirmed) return
    deleteProfile.mutate(user.id)
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]"
          />
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed z-[60] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-sm bg-bg rounded-3xl border border-white/[0.06] shadow-glass p-6"
          >
            <div className="flex flex-col items-center text-center">
              <div className="w-14 h-14 rounded-full bg-danger/10 flex items-center justify-center mb-4">
                <AlertTriangle size={24} className="text-danger" />
              </div>

              <h3 className="text-lg font-display font-bold text-text mb-2">
                {t('deleteAccount.title')}
              </h3>
              <p className="text-sm text-text-hint leading-relaxed mb-4">
                {t('deleteAccount.description')}
              </p>

              <p className="text-xs text-text-hint mb-3">
                {t('deleteAccount.typeConfirm')}
              </p>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={confirmWord}
                className="w-full px-4 py-3 rounded-xl bg-white/[0.04] border border-danger/20 text-text text-sm text-center placeholder:text-text-hint/30 focus:outline-none focus:border-danger/40 transition-colors font-mono"
              />

              <div className="flex gap-3 w-full mt-5">
                <Button variant="glass" size="sm" className="flex-1" onClick={onClose}>
                  {t('common.cancel')}
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  className="flex-1"
                  disabled={!isConfirmed}
                  isLoading={deleteProfile.isPending}
                  onClick={handleDelete}
                >
                  {t('deleteAccount.confirm')}
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
